/**
 * useFolders Hook
 *
 * Custom hook for folder management in the dashboard.
 * Provides a simplified interface to the folder store with common operations.
 *
 * @module features/dashboard/hooks/useFolders
 * @see SPEC-016 Requirement 2.1
 */

import { useCallback, useEffect, useState } from 'react';
import type {
  FolderNode,
  CreateFolderRequestInput,
  RenameFolderRequestInput,
} from '@linkode/shared';
import {
  useFolderStore,
  selectFolderTree,
  selectSelectedFolderId,
  selectIsLoading,
  selectError,
} from '../store/folderStore';
import { useDashboardStore } from '../store/dashboardStore';

// ============================================================================
// Types
// ============================================================================

export interface UseFoldersReturn {
  // State
  folderTree: FolderNode[];
  selectedFolderId: string | null;
  isLoading: boolean;
  error: ReturnType<typeof selectError>;

  // Dialog State
  isCreateDialogOpen: boolean;
  isRenameDialogOpen: boolean;
  renamingFolder: FolderNode | null;

  // Actions
  fetchFolders: () => Promise<void>;
  createFolder: (request: CreateFolderRequestInput) => Promise<string>;
  renameFolder: (folderId: string, request: RenameFolderRequestInput) => Promise<void>;
  deleteFolder: (folderId: string) => Promise<void>;
  selectFolder: (folderId: string | null) => void;
  toggleFolderExpansion: (folderId: string) => void;
  clearError: () => void;

  // Dialog Actions
  openCreateDialog: () => void;
  closeCreateDialog: () => void;
  openRenameDialog: (folder: FolderNode) => void;
  closeRenameDialog: () => void;
}

// ============================================================================
// Hook
// ============================================================================

/**
 * useFolders Hook
 *
 * Provides a unified interface for folder operations.
 *
 * Features:
 * - Auto-fetch folders on mount
 * - Folder tree management
 * - Create/rename/delete operations
 * - Dialog state management
 * - Syncs folder selection with dashboard store
 *
 * @example
 * ```tsx
 * function FolderSidebarContainer() {
 *   const {
 *     folderTree,
 *     selectedFolderId,
 *     selectFolder,
 *     toggleFolderExpansion,
 *     openCreateDialog,
 *   } = useFolders();
 *
 *   return (
 *     <FolderSidebar
 *       folderTree={folderTree}
 *       selectedFolderId={selectedFolderId}
 *       onFolderSelect={selectFolder}
 *       onFolderToggle={toggleFolderExpansion}
 *       onCreateFolder={openCreateDialog}
 *     />
 *   );
 * }
 * ```
 *
 * @see Requirement 2.1
 */
export function useFolders(): UseFoldersReturn {
  // ─────────────────────────────────────────────────────────────────────────
  // State Selectors
  // ─────────────────────────────────────────────────────────────────────────

  const folderTree = useFolderStore(selectFolderTree);
  const selectedFolderId = useFolderStore(selectSelectedFolderId);
  const isLoading = useFolderStore(selectIsLoading);
  const error = useFolderStore(selectError);

  // ─────────────────────────────────────────────────────────────────────────
  // Store Actions
  // ─────────────────────────────────────────────────────────────────────────

  const fetchFolders = useFolderStore((state) => state.fetchFolders);
  const createFolderAction = useFolderStore((state) => state.createFolder);
  const renameFolderAction = useFolderStore((state) => state.renameFolder);
  const deleteFolderAction = useFolderStore((state) => state.deleteFolder);
  const selectFolderAction = useFolderStore((state) => state.selectFolder);
  const toggleFolderExpansion = useFolderStore((state) => state.toggleFolderExpansion);
  const clearError = useFolderStore((state) => state.clearError);

  // Dashboard store for syncing folder selection
  const setCurrentFolder = useDashboardStore((state) => state.setCurrentFolder);

  // ─────────────────────────────────────────────────────────────────────────
  // Dialog State
  // ─────────────────────────────────────────────────────────────────────────

  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isRenameDialogOpen, setIsRenameDialogOpen] = useState(false);
  const [renamingFolder, setRenamingFolder] = useState<FolderNode | null>(null);

  // ─────────────────────────────────────────────────────────────────────────
  // Wrapped Actions
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Select folder and sync with dashboard store
   */
  const selectFolder = useCallback(
    (folderId: string | null) => {
      selectFolderAction(folderId);
      setCurrentFolder(folderId);
    },
    [selectFolderAction, setCurrentFolder]
  );

  /**
   * Create folder and close dialog
   */
  const createFolder = useCallback(
    async (request: CreateFolderRequestInput): Promise<string> => {
      const folderId = await createFolderAction(request);
      setIsCreateDialogOpen(false);
      return folderId;
    },
    [createFolderAction]
  );

  /**
   * Rename folder and close dialog
   */
  const renameFolder = useCallback(
    async (folderId: string, request: RenameFolderRequestInput): Promise<void> => {
      await renameFolderAction(folderId, request);
      setIsRenameDialogOpen(false);
      setRenamingFolder(null);
    },
    [renameFolderAction]
  );

  /**
   * Delete folder
   */
  const deleteFolder = useCallback(
    async (folderId: string): Promise<void> => {
      await deleteFolderAction(folderId);
    },
    [deleteFolderAction]
  );

  // ─────────────────────────────────────────────────────────────────────────
  // Dialog Actions
  // ─────────────────────────────────────────────────────────────────────────

  const openCreateDialog = useCallback(() => {
    setIsCreateDialogOpen(true);
  }, []);

  const closeCreateDialog = useCallback(() => {
    setIsCreateDialogOpen(false);
  }, []);

  const openRenameDialog = useCallback((folder: FolderNode) => {
    setRenamingFolder(folder);
    setIsRenameDialogOpen(true);
  }, []);

  const closeRenameDialog = useCallback(() => {
    setIsRenameDialogOpen(false);
    setRenamingFolder(null);
  }, []);

  // ─────────────────────────────────────────────────────────────────────────
  // Auto-fetch on mount
  // ─────────────────────────────────────────────────────────────────────────

  useEffect(() => {
    // Use the store's fetchFolders directly to avoid dependency issues
    useFolderStore.getState().fetchFolders();
  }, []); // Empty dependency array - only run once on mount

  // ─────────────────────────────────────────────────────────────────────────
  // Return
  // ─────────────────────────────────────────────────────────────────────────

  return {
    // State
    folderTree,
    selectedFolderId,
    isLoading,
    error,

    // Dialog State
    isCreateDialogOpen,
    isRenameDialogOpen,
    renamingFolder,

    // Actions
    fetchFolders,
    createFolder,
    renameFolder,
    deleteFolder,
    selectFolder,
    toggleFolderExpansion,
    clearError,

    // Dialog Actions
    openCreateDialog,
    closeCreateDialog,
    openRenameDialog,
    closeRenameDialog,
  };
}
